/**
 * VariableLineageDrawer — Citation_Traceability_Architecture CT5.
 *
 * Extends the LB3 amendment-history drawer so it renders the FULL
 * lineage chain (amendments + supporting docs + provider data +
 * Site Context claims) for one SED variable, newest-first.
 *
 * Today LB3-2 ships a drawer that lists ONLY user amendments. CT5
 * keeps that surface but inlines the rest of the lineage so the
 * audit trail is complete from a single click:
 *
 *   site.developableAcres
 *   ──────────────────────────────────────────────────────────
 *   Current: 187 ac · Confidence: 84%
 *
 *   Lineage (newest first):
 *   ┌─── 🧑 User amendment · W. Theron · 2026-05-12 ──┐
 *   │ 240 → 187 ac · "Regrid override"               │
 *   ├─── 📄 Document · Borehole_Survey.pdf ──────────┤
 *   │ "Total site: 240 ac; substation: 18 ac"        │
 *   │ Score: 78%                                     │
 *   ├─── 🌐 Provider · Regrid ───────────────────────┤
 *   │ Returned parcel area: 187.34 ac                │
 *   └────────────────────────────────────────────────┘
 *
 * Reads exclusively via the CT2 pure resolver — the projection logic
 * is NOT re-implemented here. The drawer just sorts the resolver's
 * output by timestamp and renders each variant with a kind-specific
 * row.
 */
import React, { useEffect, useMemo, useState } from 'react';
import { FileText, Globe, User, Sparkles, Layers, History, RotateCcw, ExternalLink, GitBranch } from 'lucide-react';
import type { SiteDocument } from '../../types/siteRag';
import type { SiteEvidenceDocument } from '../../types/siteEvidence';
import type { SiteContext } from '../../types/siteContext';
import type { SEDAmendment } from '../../types/sedAmendments';
import { resolveEvidenceLineage } from '../../lib/useEvidenceLineage';
import type { EvidenceSource, EvidenceLineage } from '../../types/citation';
import { cleanDocFileName } from '../../lib/docFileName';
import {
  projectVariableResolutionHistory,
  projectVariableResolutionHistoryFromAmendments,
  RESOLUTION_STRATEGY_LABEL,
  type VariableResolutionHistoryEntry,
} from '../../lib/variableResolutionHistory';
import type { AuditLogEvent } from '../../types/auditLog';
import { ResponsiveTabStrip } from '../ui/responsive-tab-strip';

export interface VariableLineageDrawerProps {
  /** The variable path being inspected (e.g. 'seismic.data.pga'). */
  variablePath: string;
  /** The variable's display label — surfaced in the header. */
  label: string;
  /** The current displayed value. */
  currentValue?: unknown;
  /** Unit label, optional. */
  unit?: string;
  /** Active SED — null short-circuits to empty. */
  sed: SiteEvidenceDocument | null;
  /** Amendments scoped to this SED's id; the resolver filters by path. */
  amendments: SEDAmendment[];
  /** Site Context with claims, or null. */
  siteContext?: SiteContext | null;
  /** Site documents used to resolve doc references. */
  documents?: SiteDocument[];
  /** Optional test-id suffix (defaults to the path). */
  testIdSuffix?: string;
  /**
   * SET-B2 — Site Evidence → Documents bridge. When provided, the
   * filename on each `kind === 'document'` lineage row becomes a
   * clickable button that calls this handler with the source's docId.
   * The host typically responds by flipping the active tab to
   * 'documents' and writing `?focus=doc:<id>` so DocumentLibrary's
   * focus receiver scrolls + opens the DetailDrawer for the doc.
   * Absence of this prop keeps the filename as plain text (legacy
   * mounts without a Documents tab destination).
   */
  onOpenDocument?: (docId: string) => void;
  /**
   * UF-3 (UFC-FOLLOWUP-RESOLUTION-HISTORY) — page fetcher for the
   * History tab. Injected so the drawer doesn't import the Firestore
   * boundary directly and so vitest can pin the projection without
   * mocking the audit boundary. The drawer calls this once when the
   * tab first opens; subsequent re-opens reuse the cached result
   * (effects are de-duped via the `historyFetched` state guard).
   *
   * Default implementation (when the prop is omitted) lazily imports
   * `auditLogService.fetchAuditLogPage` so production callers get the
   * Firestore-backed timeline without extra plumbing. Caller may
   * override to widen the date range or apply company-scoping.
   */
  fetchAuditEvents?: () => Promise<AuditLogEvent[]>;
}

/* ── Helpers ─────────────────────────────────────────────────────────── */

/**
 * Extract a sortable timestamp from an EvidenceSource. When the source
 * carries no timestamp (derived, regional-baseline, document), return
 * the empty string — those rows fall to the end of the newest-first
 * ordering rather than to the front.
 */
function timestampOf(s: EvidenceSource): string {
  switch (s.kind) {
    case 'user-amendment':     return s.amendedAt;
    case 'site-context-claim': return s.capturedAt;
    case 'provider':           return s.capturedAt;
    case 'document':           return '';
    case 'regional-baseline':  return '';
    case 'derived':            return '';
  }
}

const KIND_ICON: Record<EvidenceSource['kind'], React.ReactNode> = {
  'document': <FileText className="w-3 h-3 shrink-0" aria-hidden />,
  'provider': <Globe className="w-3 h-3 shrink-0" aria-hidden />,
  'user-amendment': <User className="w-3 h-3 shrink-0" aria-hidden />,
  'site-context-claim': <Sparkles className="w-3 h-3 shrink-0" aria-hidden />,
  'regional-baseline': <Globe className="w-3 h-3 shrink-0" aria-hidden />,
  'derived': <Layers className="w-3 h-3 shrink-0" aria-hidden />,
};

const KIND_LABEL: Record<EvidenceSource['kind'], string> = {
  'document': 'Document',
  'provider': 'Provider',
  'user-amendment': 'User amendment',
  'site-context-claim': 'Site Context claim',
  'regional-baseline': 'Regional baseline',
  'derived': 'Derived',
};

/* ── Per-source row sub-component ────────────────────────────────────── */

interface RowProps {
  source: EvidenceSource;
  /** Resolve a docId to its filename, when available. */
  resolveDocName: (docId: string) => string;
  /** Resolve an amendmentId to its full record (oldValue/newValue/reason). */
  resolveAmendment: (id: string) => SEDAmendment | undefined;
  testKey: string;
  /** SET-B2 — when supplied, document rows render the filename as a
   *  clickable button that calls back with the source docId. */
  onOpenDocument?: (docId: string) => void;
}

const LineageRow: React.FC<RowProps> = ({ source, resolveDocName, resolveAmendment, testKey, onOpenDocument }) => {
  const stamp = timestampOf(source);
  const stampLabel = stamp ? stamp.slice(0, 10) : null;

  return (
    <div
      className="type-body text-text-primary border-l-2 border-primary/30 pl-2 py-1"
      data-testid={`variable-lineage-row-${testKey}`}
      data-kind={source.kind}
    >
      <div className="flex items-center gap-1 text-text-secondary uppercase tracking-widest type-meta">
        {KIND_ICON[source.kind]}
        <span>{KIND_LABEL[source.kind]}</span>
        {stampLabel && (
          <span className="text-text-muted font-mono">· {stampLabel}</span>
        )}
      </div>
      {/* kind-specific body */}
      {source.kind === 'user-amendment' && (() => {
        const full = resolveAmendment(source.amendmentId);
        // Prefer the on-record email over the union's raw uid — that's
        // what the legacy LB3-2 drawer surfaced and what the audit
        // tests pin against. Falls back to uid when no email is on file.
        const actorLabel = full?.createdByEmail || source.userId;
        const isRevertRow = Boolean(full?.revertedFromAmendmentId) || Boolean(full?.id?.startsWith('revert-'));
        const statusBadge = full?.status
          ? isRevertRow
            ? `revert (${full.status})`
            : full.status
          : null;
        return (
          <div className="type-body text-text-primary">
            {full && (
              <span className="font-mono text-text-secondary">
                {String(full.oldValue ?? '—')} → {String(full.newValue ?? '—')}
                {full.unit ? ` ${full.unit}` : ''}
              </span>
            )}
            {statusBadge && (
              <span
                className={`font-mono uppercase text-[9px] ml-1 px-1 py-0.5 rounded border ${
                  full?.status === 'pending'
                    ? 'text-warning border-warning/40 bg-warning/10'
                    : 'text-text-muted border-border'
                }`}
              >
                {statusBadge}
              </span>
            )}
            {(full?.reason || source.note) && (
              <span className="italic text-text-secondary"> · "{full?.reason ?? source.note}"</span>
            )}
            <span className="text-text-muted"> · {actorLabel}</span>
            {full?.source?.label && !full?.source?.url && (
              <span className="text-text-muted"> · {full.source.label}</span>
            )}
            {full?.source?.url && (
              <>
                {' · '}
                <a
                  href={full.source.url}
                  target="_blank"
                  rel="noreferrer"
                  className="text-primary underline break-all"
                >
                  {full.source.url}
                </a>
              </>
            )}
          </div>
        );
      })()}

      {source.kind === 'document' && (
        <div className="type-body text-text-primary">
          {/* SET-B2 — when the host provides the bridge handler, render
              the filename as a button that flips to the Documents tab
              with focus=doc:<id>. Plain span otherwise so legacy mounts
              (no Documents-tab destination) stay quiet. */}
          {onOpenDocument ? (
            <button
              type="button"
              onClick={() => onOpenDocument(source.docId)}
              className="font-mono text-primary hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-1"
              data-testid={`variable-lineage-row-${testKey}-open-doc`}
              aria-label={`Open document ${resolveDocName(source.docId)} in the Documents tab`}
            >
              {resolveDocName(source.docId)}
            </button>
          ) : (
            <span className="font-mono">{resolveDocName(source.docId)}</span>
          )}
          {source.relevanceScore != null && (
            <span className="text-text-muted"> · {Math.round(source.relevanceScore * 100)}% relevance</span>
          )}
          {source.quote && (
            <div className="pl-2 mt-0.5 italic text-text-secondary">
              "{source.quote}"
            </div>
          )}
        </div>
      )}

      {source.kind === 'provider' && (
        <div className="type-body text-text-primary">
          <span className="font-mono">{source.providerKey}</span>
          {/* Provider value is unknown by design — render a one-line
              JSON-ish probe so the user sees SOMETHING for debugging
              without leaking complex objects to the eye. */}
          {source.value != null && (
            <span className="text-text-muted">
              {' '}returned <span className="font-mono">{summariseProviderValue(source.value)}</span>
            </span>
          )}
        </div>
      )}

      {source.kind === 'site-context-claim' && (
        <div className="type-body text-text-primary">
          <span className="font-mono">{source.field}</span>
          <span className="text-text-muted"> · declared via Site Context</span>
        </div>
      )}

      {source.kind === 'regional-baseline' && (
        <div className="type-body text-text-primary">
          <span className="font-mono">{source.baselineKey}</span>
          <span className="text-text-muted"> · {Math.round(source.credibility * 100)}% credibility</span>
        </div>
      )}

      {source.kind === 'derived' && (
        <div className="type-body text-text-primary">
          <span className="text-text-muted italic">
            Derived from {source.from.length} source{source.from.length === 1 ? '' : 's'}
          </span>
          {source.rationale && (
            <span className="italic text-text-secondary"> · {source.rationale}</span>
          )}
        </div>
      )}
    </div>
  );
};

/** Coarse summary for an unknown provider value — keeps the drawer compact. */
function summariseProviderValue(value: unknown): string {
  if (value == null) return '—';
  if (typeof value === 'number' || typeof value === 'string' || typeof value === 'boolean') {
    return String(value);
  }
  if (Array.isArray(value)) return `[${value.length} items]`;
  try {
    const s = JSON.stringify(value);
    return s.length > 80 ? `${s.slice(0, 77)}…` : s;
  } catch {
    return '<object>';
  }
}

/* ── Component ───────────────────────────────────────────────────────── */

/** UF-3 — default fetcher. Lazily imports the audit log service so
 *  the drawer's main lineage path never pays the Firestore boundary
 *  cost on mount (the History tab triggers the import on first open).
 *
 *  Returns up to 100 events from the most recent month — long
 *  enough to surface the entire amendment timeline for any one
 *  variable without paginating; the UI shows a "Load older" CTA if
 *  the page is full. */
async function defaultFetchAuditEvents(): Promise<AuditLogEvent[]> {
  const { fetchAuditLogPage } = await import('../../services/auditLogService');
  const page = await fetchAuditLogPage({ pageSize: 100 });
  return page.events;
}

export const VariableLineageDrawer: React.FC<VariableLineageDrawerProps> = ({
  variablePath,
  label,
  currentValue,
  unit,
  sed,
  amendments,
  siteContext,
  documents,
  testIdSuffix,
  onOpenDocument,
  fetchAuditEvents,
}) => {
  // UF-3 — tab state + History fetch state. `'lineage'` is the
  // default (preserves the pre-UF-3 surface; existing tests rely on
  // the lineage rows being visible without an interaction).
  const [activeTab, setActiveTab] = useState<'lineage' | 'history'>('lineage');
  const [historyEntries, setHistoryEntries] = useState<VariableResolutionHistoryEntry[] | null>(null);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [historyError, setHistoryError] = useState<string | null>(null);
  const [historyFetched, setHistoryFetched] = useState(false);
  const lineage = useMemo<EvidenceLineage>(
    () => resolveEvidenceLineage(
      { kind: 'sed-variable', id: variablePath },
      {
        sed,
        amendments,
        siteContext: siteContext ?? null,
        documents,
      },
    ),
    [variablePath, sed, amendments, siteContext, documents],
  );

  // Newest-first chronology. Sources without a timestamp drop to the
  // end so the chain reads as the audit trail the spec calls out.
  const orderedSources = useMemo(() => {
    return [...lineage.sources].sort((a, b) => {
      const ta = timestampOf(a);
      const tb = timestampOf(b);
      if (!ta && !tb) return 0;
      if (!ta) return 1;
      if (!tb) return -1;
      return tb.localeCompare(ta); // descending ISO order = newest first
    });
  }, [lineage.sources]);

  const docById = useMemo(() => {
    const m = new Map<string, SiteDocument>();
    for (const d of documents ?? []) m.set(d.id, d);
    return m;
  }, [documents]);
  const resolveDocName = (id: string) => {
    const doc = docById.get(id);
    return doc ? cleanDocFileName(doc.fileName) : id;
  };

  const amendmentById = useMemo(() => {
    const m = new Map<string, SEDAmendment>();
    for (const a of amendments) m.set(a.id, a);
    return m;
  }, [amendments]);
  const resolveAmendment = (id: string) => amendmentById.get(id);

  const suffix = testIdSuffix ?? variablePath;

  // Authoritative resolution history projected reactively from the current
  // site/SED amendment records already loaded by the ordinary route.
  const authoritativeAmendmentHistory = useMemo<VariableResolutionHistoryEntry[]>(
    () => projectVariableResolutionHistoryFromAmendments(amendments, variablePath, sed?.id),
    [amendments, variablePath, sed?.id],
  );
  const usesInjectedAuditFetcher = typeof fetchAuditEvents === 'function';

  // UF-3 — lazy History fetch when an explicit `fetchAuditEvents` prop is
  // injected (unit tests). Normal runtime uses `authoritativeAmendmentHistory`
  // directly without an unscoped cross-site `audit_log` query.
  useEffect(() => {
    if (!usesInjectedAuditFetcher) return;
    if (activeTab !== 'history') return;
    if (historyFetched) return;
    let cancelled = false;
    setHistoryLoading(true);
    setHistoryError(null);
    fetchAuditEvents()
      .then(events => {
        if (cancelled) return;
        setHistoryEntries(projectVariableResolutionHistory(events, variablePath));
        setHistoryFetched(true);
      })
      .catch((e: unknown) => {
        if (cancelled) return;
        console.warn('[VariableLineageDrawer] history fetch failed:', (e as Error)?.message);
        setHistoryError((e as Error)?.message ?? 'failed');
        setHistoryFetched(true);
      })
      .finally(() => {
        if (cancelled) return;
        setHistoryLoading(false);
      });
    return () => { cancelled = true; };
  }, [usesInjectedAuditFetcher, activeTab, historyFetched, variablePath, fetchAuditEvents]);

  const effectiveHistoryEntries = usesInjectedAuditFetcher
    ? historyEntries
    : authoritativeAmendmentHistory;
  const effectiveHistoryLoading = usesInjectedAuditFetcher ? historyLoading : false;
  const effectiveHistoryError = usesInjectedAuditFetcher ? historyError : null;

  return (
    <div
      className="mt-2 p-2 border-l-2 border-primary/40 bg-state-hover/40 space-y-1.5"
      data-testid={`variable-lineage-drawer-${suffix}`}
    >
      <div className="flex items-center justify-between gap-2">
        <div className="type-meta uppercase tracking-widest text-text-muted">
          {activeTab === 'lineage'
            ? `Lineage (${orderedSources.length} source${orderedSources.length === 1 ? '' : 's'}, newest first)`
            : 'Resolution history'}
        </div>
        <div className="type-meta uppercase tracking-widest text-text-muted">
          Confidence: {Math.round(lineage.confidence * 100)}%
        </div>
      </div>
      <div className="type-body text-text-secondary">
        {label}
        {currentValue !== undefined && currentValue !== null && (
          <>
            {' · '}
            <span className="font-mono text-text-primary">
              {Array.isArray(currentValue) ? currentValue.join(', ') : String(currentValue)}
            </span>
            {unit && <span className="text-text-muted"> {unit}</span>}
          </>
        )}
      </div>
      {/* UF-3 — tab nav. Default lands on Lineage so existing
          callers + tests stay green; clicking History shows the
          authoritative resolution timeline. */}
      <ResponsiveTabStrip
        ariaLabel="Variable lineage views"
        testId={`variable-lineage-drawer-tabs-${suffix}`}
        tabs={[
          {
            id: 'lineage',
            label: 'Lineage',
            icon: GitBranch,
            testId: `variable-lineage-drawer-tab-lineage-${suffix}`,
          },
          {
            id: 'history',
            label: 'History',
            icon: History,
            count: effectiveHistoryEntries?.length,
            testId: `variable-lineage-drawer-tab-history-${suffix}`,
          },
        ]}
        activeId={activeTab}
        onSelect={id => setActiveTab(id as 'lineage' | 'history')}
      />

      {activeTab === 'lineage' && (
        orderedSources.length === 0 ? (
          <div
            className="type-body italic text-text-muted"
            data-testid={`variable-lineage-drawer-empty-${suffix}`}
          >
            No provenance on record for this variable.
          </div>
        ) : (
          <div className="space-y-1">
            {orderedSources.map((s, i) => (
              <LineageRow
                key={`${s.kind}::${i}`}
                source={s}
                resolveDocName={resolveDocName}
                resolveAmendment={resolveAmendment}
                testKey={`${suffix}-${i}`}
                onOpenDocument={onOpenDocument}
              />
            ))}
          </div>
        )
      )}

      {activeTab === 'history' && (
        <ResolutionHistoryList
          entries={effectiveHistoryEntries}
          loading={effectiveHistoryLoading}
          error={effectiveHistoryError}
          unit={unit}
          testIdSuffix={suffix}
        />
      )}
    </div>
  );
};

/* ── UF-3 — Resolution History list ──────────────────────────────────── */

interface ResolutionHistoryListProps {
  entries: VariableResolutionHistoryEntry[] | null;
  loading: boolean;
  error: string | null;
  unit?: string;
  testIdSuffix: string;
}

/**
 * UF-3 — render the projected timeline. Loading / error / empty are
 * all explicitly handled so the user always sees a status, never a
 * blank pane.
 */
const ResolutionHistoryList: React.FC<ResolutionHistoryListProps> = ({
  entries,
  loading,
  error,
  unit,
  testIdSuffix,
}) => {
  if (loading) {
    return (
      <div
        className="type-body italic text-text-muted"
        data-testid={`variable-lineage-drawer-history-loading-${testIdSuffix}`}
      >
        Loading resolution history…
      </div>
    );
  }
  if (error) {
    return (
      <div
        className="type-body italic text-warning"
        data-testid={`variable-lineage-drawer-history-error-${testIdSuffix}`}
      >
        Couldn't load resolution history ({error}). Try again later.
      </div>
    );
  }
  if (!entries || entries.length === 0) {
    return (
      <div
        className="type-body italic text-text-muted"
        data-testid={`variable-lineage-drawer-history-empty-${testIdSuffix}`}
      >
        No prior resolutions recorded for this variable.
      </div>
    );
  }
  return (
    <ul
      className="space-y-1"
      data-testid={`variable-lineage-drawer-history-list-${testIdSuffix}`}
    >
      {entries.map(e => (
        <ResolutionHistoryRow key={e.id} entry={e} unit={unit} testIdSuffix={testIdSuffix} />
      ))}
    </ul>
  );
};

interface ResolutionHistoryRowProps {
  entry: VariableResolutionHistoryEntry;
  unit?: string;
  testIdSuffix: string;
}

const ResolutionHistoryRow: React.FC<ResolutionHistoryRowProps> = ({ entry, unit, testIdSuffix }) => {
  const isRevert = entry.strategy === 'reverted';
  const Icon = isRevert ? RotateCcw : User;
  const valueLabel = (v: unknown): string => {
    if (v == null) return '—';
    if (typeof v === 'string') return v.trim() || '—';
    if (typeof v === 'number' || typeof v === 'boolean') return String(v);
    try { return JSON.stringify(v); } catch { return String(v); }
  };
  return (
    <li
      className={`type-body text-text-primary border-l-2 pl-2 py-1 ${
        isRevert ? 'border-warning/40' : 'border-primary/30'
      }`}
      data-testid={`variable-lineage-drawer-history-row-${testIdSuffix}-${entry.id}`}
      data-strategy={entry.strategy}
    >
      <div className="flex items-center gap-1 text-text-secondary uppercase tracking-widest type-meta">
        <Icon className="w-3 h-3 shrink-0" aria-hidden="true" />
        <span>{RESOLUTION_STRATEGY_LABEL[entry.strategy]}</span>
        {entry.lifecycleStatus && entry.strategy !== 'reverted' && entry.lifecycleStatus !== 'applied' && (
          <span className="text-text-muted font-mono">· {entry.lifecycleStatus}</span>
        )}
        {typeof entry.sedVersion === 'number' && (
          <span className="text-text-muted font-mono">· v{entry.sedVersion}</span>
        )}
        {entry.when && (
          <span className="text-text-muted font-mono">· {entry.when.slice(0, 10)}</span>
        )}
      </div>
      <div className="type-body text-text-primary">
        <span className="font-mono text-text-secondary">
          {valueLabel(entry.priorValue)} → {valueLabel(entry.newValue)}
          {unit ? ` ${unit}` : ''}
        </span>
        {entry.reason && (
          <span className="italic text-text-secondary"> · "{entry.reason}"</span>
        )}
        <span className="text-text-muted"> · {entry.actor}</span>
        {entry.sourceLabel && !entry.sourceUrl && (
          <span className="text-text-muted"> · {entry.sourceLabel}</span>
        )}
        {entry.sourceUrl && (
          <>
            {' · '}
            <a
              href={entry.sourceUrl}
              target="_blank"
              rel="noreferrer"
              className="text-primary underline break-all inline-flex items-center gap-0.5"
              data-testid={`variable-lineage-drawer-history-source-link-${testIdSuffix}-${entry.id}`}
            >
              {entry.sourceLabel || entry.sourceUrl}
              <ExternalLink className="w-2.5 h-2.5" aria-hidden="true" />
            </a>
          </>
        )}
      </div>
    </li>
  );
};
